import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/use-toast'
import { putFetcher } from 'api/swr'
import { Trash2 } from 'lucide-react'
import { useState, type MouseEvent } from 'react'

type RemoveDefaultSetAvatarButtonProps = {
  avatarId: string
  onRemoved: () => void | Promise<void>
}

export function RemoveDefaultSetAvatarButton({
  avatarId,
  onRemoved,
}: RemoveDefaultSetAvatarButtonProps) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)

  const handleConfirm = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (pending) return
    setPending(true)
    try {
      await putFetcher(`/avatars/${avatarId}`, {
        arg: { key: 'defaultSet', value: '0' },
      })
      toast({
        title: '已移除套装',
        description: avatarId,
        className: 'bg-green-500 text-white',
      })
      setOpen(false)
      await onRemoved()
    } catch (err: unknown) {
      toast({
        variant: 'destructive',
        title: 'Failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 shrink-0 border-destructive bg-background text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          setOpen(true)
        }}
        title="Remove outfit"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!pending) setOpen(next)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Are you sure you want to remove this outfit?</DialogTitle>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={pending}
            >
              {pending ? 'Processing...' : 'Confirm remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
